import React, { useState } from 'react';
import { Card, Row, Col, Button, Modal, Tabs, Tag, Space, Typography, Tooltip } from 'antd';
import { EyeOutlined, CheckOutlined } from '@ant-design/icons';
import { gradientTemplates, getGradientsByCategory, getAllGradientCategories, GradientTemplate } from '../../data/gradientTemplates';

const { Title, Text } = Typography;

interface GradientTemplateSelectorProps {
  onSelect: (template: GradientTemplate) => void;
  onPreview?: (template: GradientTemplate) => void;
}

const GradientTemplateSelector: React.FC<GradientTemplateSelectorProps> = ({ onSelect, onPreview }) => {
  const [visible, setVisible] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<GradientTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<GradientTemplate | null>(null);

  const categories = getAllGradientCategories();

  const handleSelect = (template: GradientTemplate) => {
    setSelectedTemplate(template);
    onSelect(template);
    setVisible(false);
  };

  const handlePreview = (template: GradientTemplate) => {
    setPreviewTemplate(template);
    if (onPreview) {
      onPreview(template);
    }
  };

  const renderGradientPreview = (template: GradientTemplate) => (
    <div
      style={{
        width: '100%',
        height: '60px',
        background: template.gradient,
        borderRadius: '8px',
        border: '1px solid #d9d9d9',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'white',
          fontSize: '12px',
          fontWeight: 'bold',
          textShadow: '0 1px 2px rgba(0,0,0,0.5)',
          textAlign: 'center'
        }}
      >
        {template.name}
      </div>
    </div>
  );

  const renderTemplateCard = (template: GradientTemplate) => (
    <Col xs={24} sm={12} md={8} lg={6} key={template.name}>
      <Card
        hoverable
        size="small"
        style={{
          marginBottom: '16px',
          border: selectedTemplate?.name === template.name ? '2px solid #1890ff' : '1px solid #d9d9d9'
        }}
        actions={[
          <Tooltip title="预览">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handlePreview(template)}
            />
          </Tooltip>,
          <Tooltip title="选择">
            <Button
              type="text"
              icon={<CheckOutlined />}
              onClick={() => handleSelect(template)}
            />
          </Tooltip>
        ]}
      >
        {renderGradientPreview(template)}
        <div style={{ marginTop: '8px' }}>
          <Text strong style={{ fontSize: '12px' }}>{template.name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '10px' }}>{template.description}</Text>
        </div>
      </Card>
    </Col>
  );

  const renderCategoryTab = (category: string) => {
    const templates = getGradientsByCategory(category);
    return (
      <Row gutter={[16, 16]}>
        {templates.map(renderTemplateCard)}
      </Row>
    );
  };

  return (
    <>
      <Button
        type="dashed"
        onClick={() => setVisible(true)}
        style={{ width: '100%' }}
      >
        选择渐变色模板
      </Button>

      <Modal
        title="渐变色模板选择器"
        open={visible}
        onCancel={() => setVisible(false)}
        width={1000}
        footer={null}
        bodyStyle={{ padding: '16px' }}
      >
        <Tabs
          defaultActiveKey={categories[0]}
          items={categories.map(category => ({
            key: category,
            label: category,
            children: renderCategoryTab(category)
          }))}
        />
      </Modal>

      {previewTemplate && (
        <Modal
          title={`预览: ${previewTemplate.name}`}
          open={!!previewTemplate}
          onCancel={() => setPreviewTemplate(null)}
          footer={[
            <Button key="cancel" onClick={() => setPreviewTemplate(null)}>
              关闭
            </Button>,
            <Button
              key="select"
              type="primary"
              onClick={() => handleSelect(previewTemplate)}
            >
              选择此模板
            </Button>
          ]}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text strong>模板名称:</Text> {previewTemplate.name}
            </div>
            <div>
              <Text strong>描述:</Text> {previewTemplate.description}
            </div>
            <div>
              <Text strong>渐变代码:</Text>
              <div style={{ 
                background: '#f5f5f5', 
                padding: '8px', 
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '12px',
                marginTop: '4px'
              }}>
                {previewTemplate.gradient}
              </div>
            </div>
            <div>
              <Text strong>方向:</Text> {previewTemplate.direction}
            </div>
            <div>
              <Text strong>颜色:</Text>
              <Space wrap style={{ marginTop: '4px' }}>
                {previewTemplate.colors.map((color, index) => (
                  <Tag key={index} color={color}>
                    {color}
                  </Tag>
                ))}
              </Space>
            </div>
            <div>
              <Text strong>预览效果:</Text>
              <div style={{ marginTop: '8px' }}>
                {renderGradientPreview(previewTemplate)}
              </div>
            </div>
          </Space>
        </Modal>
      )}
    </>
  );
};

export default GradientTemplateSelector;





